import {
  AutocompleteInput,
  DateTimePickerInput,
  DefaultTextInput,
  FreeSoloDropdownInput,
} from "@/components/Inputs";
import "./StaticPriceOverrideUploadModal.styles.scss";

// MUI Imports
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import { styled } from "@mui/material/styles";

// Type Imports
import { ISelectOption } from "@pulse-ui/types/components/DropdownInput";
import { IStaticPriceOverrideUploadModalStatus } from "@/pages/StaticPriceOverrides/StaticPriceOverrides.types";
import dayjs from "dayjs";

const CustomDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const StaticPriceOverrideUploadModal = ({
  staticPriceOverrideUploadModalStatus,
  errors,
  instrumentsOptions,
  setStaticPriceOverrideUploadModalStatus,
  handleClose,
  handlePostStaticPriceOverride,
  handleUpdateStaticPriceOverride,
  handleRemoveScheduleFromStaticPriceOverride,
}: {
  staticPriceOverrideUploadModalStatus: IStaticPriceOverrideUploadModalStatus;
  errors: {
    symbol: boolean;
    unique_symbol: boolean;
    start_time: boolean;
    end_time: boolean;
    day: boolean;
    price: boolean;
  };
  instrumentsOptions: ISelectOption[];
  setStaticPriceOverrideUploadModalStatus: React.Dispatch<
    React.SetStateAction<IStaticPriceOverrideUploadModalStatus>
  >;
  handleClose: () => void;
  handlePostStaticPriceOverride: () => Promise<void>;
  handleUpdateStaticPriceOverride: () => Promise<void>;
  handleRemoveScheduleFromStaticPriceOverride: () => Promise<void>;
}) => {
  // Methods - START -----------------------------------
  const handleDateTimePickerChange = (
    newValue: any,
    field: "start" | "end"
  ) => {
    if (field === "start") {
      setStaticPriceOverrideUploadModalStatus((prev: any) => {
        return {
          ...prev,
          start_time: newValue,
        };
      });
    } else {
      setStaticPriceOverrideUploadModalStatus((prev: any) => {
        return {
          ...prev,
          end_time: newValue,
        };
      });
    }
  };

  const handleSymbolAutocompleteDropdownChange = (
    _: React.SyntheticEvent,
    newValue: ISelectOption | null
  ) => {
    if (newValue) {
      setStaticPriceOverrideUploadModalStatus((prev: any) => {
        return {
          ...prev,
          symbol: newValue.value,
        };
      });
    } else {
      setStaticPriceOverrideUploadModalStatus((prev: any) => {
        return {
          ...prev,
          symbol: "",
        };
      });
    }
  };
  // Methods - END -------------------------------------

  return (
    <CustomDialog
      onClose={handleClose}
      open={true}
      className="static-price-override-upload-modal-dialog"
      maxWidth="xl"
      PaperProps={{
        elevation: 0,
        sx: {
          boxShadow: "0px 0px 4px #777",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          },
        },
      }}
    >
      <DialogTitle
        sx={{ m: 0, p: 2 }}
        className="static-price-override-upload-modal-dialog-title"
      >
        Save Static Price Override
      </DialogTitle>
      <DialogContent
        dividers
        className="static-price-override-upload-modal-dialog-content"
      >
        <div className="static-price-override-upload-modal-dialog-content-container">
          <p className="pulse-typography-subtitle-1 pulse-mb-2">
            Confirm the details below before submitting.
          </p>

          <AutocompleteInput
            value={{
              label: staticPriceOverrideUploadModalStatus.symbol || "",
              value: staticPriceOverrideUploadModalStatus.symbol || "",
            }}
            inputLabel="Symbol"
            onChange={handleSymbolAutocompleteDropdownChange}
            disabled={instrumentsOptions.length === 0}
            options={instrumentsOptions}
          />
          {errors.symbol && (
            <p className="pulse-typography-overline pulse-color--error-300 pulse-mb-2">
              Symbol is required..
            </p>
          )}
          {errors.unique_symbol && (
            <p className="pulse-typography-overline pulse-color--error-300 pulse-mb-2">
              Symbol already has price overrides in place.
            </p>
          )}

          <div className="static-price-override-upload-modal-dialog-content-container-details pulse-mt-2">
            <FreeSoloDropdownInput
              key="day"
              name="day"
              label="Day"
              options={[
                {
                  label: "MONDAY",
                  value: "MONDAY",
                },
                {
                  label: "TUESDAY",
                  value: "TUESDAY",
                },
                {
                  label: "WEDNESDAY",
                  value: "WEDNESDAY",
                },
                {
                  label: "THURSDAY",
                  value: "THURSDAY",
                },
                {
                  label: "FRIDAY",
                  value: "FRIDAY",
                },
                {
                  label: "SATURDAY",
                  value: "SATURDAY",
                },
                {
                  label: "SUNDAY",
                  value: "SUNDAY",
                },
              ]}
              value={staticPriceOverrideUploadModalStatus.day || ""}
              onChange={(e: any) => {
                setStaticPriceOverrideUploadModalStatus((prev: any) => {
                  return {
                    ...prev,
                    day: e,
                  };
                });
              }}
              containerClassNames="free-solo-dropdown-input pulse-br-sm pulse-mb-2"
              labelClassNames={`pulse-color--background-50 pulse-typography-body-2 pulse-mb-1`}
            />
            {errors.day && (
              <p className="pulse-typography-overline pulse-color--error-300 pulse-mb-2">
                Weekday is required
              </p>
            )}

            <FormGroup className="pulse-mb-2">
              <FormControlLabel
                control={<Checkbox />}
                label="All Day"
                onChange={(e: any) =>
                  setStaticPriceOverrideUploadModalStatus((prev: any) => {
                    return {
                      ...prev,
                      all_day: e.target.checked,
                      start_time: dayjs.utc().hour(0).minute(0).second(0),
                      end_time: dayjs.utc().hour(0).minute(0).second(0),
                    };
                  })
                }
              />
            </FormGroup>

            <DateTimePickerInput
              inputLabel="Start Time"
              handleChange={(newValue: any) =>
                handleDateTimePickerChange(newValue, "start")
              }
              startFromTimePickerValue={
                staticPriceOverrideUploadModalStatus.start_time
              }
              disabled={staticPriceOverrideUploadModalStatus.all_day}
            />
            {errors.start_time && (
              <p className="pulse-typography-overline pulse-color--error-300 pulse-mb-2">
                Start time is required, and must be before end time.
              </p>
            )}

            <DateTimePickerInput
              inputLabel="End Time"
              handleChange={(newValue: any) =>
                handleDateTimePickerChange(newValue, "end")
              }
              startFromTimePickerValue={
                staticPriceOverrideUploadModalStatus.end_time
              }
              disabled={staticPriceOverrideUploadModalStatus.all_day}
            />
            {errors.end_time && (
              <p className="pulse-typography-overline pulse-color--error-300 pulse-mb-2">
                End time is required, and must be after start time.
              </p>
            )}

            <DefaultTextInput
              name="price"
              label="Price"
              value={staticPriceOverrideUploadModalStatus.price}
              onChange={(e: any) =>
                setStaticPriceOverrideUploadModalStatus((prev: any) => {
                  return {
                    ...prev,
                    price: e.target.value,
                  };
                })
              }
              containerClassNames="pulse-default-text-input pulse-br-sm pulse-mb-2"
              labelClassNames={`pulse-color--background-50
                pulse-typography-body-2 pulse-mb-1`}
              inputClassNames="pulse-bg-color--background-800 pulse-typography-body-1"
            />
            {errors.price && (
              <p className="pulse-typography-overline pulse-color--error-300 pulse-mb-2">
                Price is required, and must be a valid decimal value.
              </p>
            )}
          </div>
        </div>
      </DialogContent>
      <DialogActions className="static-price-override-upload-modal-dialog-actions">
        {staticPriceOverrideUploadModalStatus.symbol && (
          <Button
            onClick={handleRemoveScheduleFromStaticPriceOverride}
            className="static-price-override-upload-modal-dialog-delete-button"
          >
            Delete
          </Button>
        )}

        <Button
          onClick={handleClose}
          className="static-price-override-upload-modal-dialog-close-button"
        >
          Close
        </Button>

        <Button
          onClick={() =>
            staticPriceOverrideUploadModalStatus.type === "add"
              ? handlePostStaticPriceOverride()
              : handleUpdateStaticPriceOverride()
          }
          className="static-price-override-upload-modal-dialog-submit-button"
        >
          Submit
        </Button>
      </DialogActions>
    </CustomDialog>
  );
};

export default StaticPriceOverrideUploadModal;
